import React, { useState } from 'react';
import { X, AlertTriangle, Mail, MessageSquare, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentFailureModal = ({ 
  isOpen, 
  onClose, 
  customer, 
  amount, 
  paymentMethod,
  onSuccess 
}) => {
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleSendInvoice = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/payments/handle-failure`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_id: customer.id,
          payment_method: paymentMethod || 'card',
          amount: amount,
          send_email: sendEmail,
          send_sms: sendSms
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (onSuccess) onSuccess(data);
        onClose();
      } else {
        setError(data.detail || 'Failed to process');
      }
    } catch (err) {
      setError('Network error - please try again');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="p-6 border-b border-zinc-100">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 rounded-full">
              <AlertTriangle size={24} className="text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-zinc-900">Payment Failed</h2>
              <p className="text-sm text-zinc-500">
                {customer?.name || 'Customer'}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="p-4 bg-red-50 rounded-xl border border-red-200">
            <p className="text-sm text-red-800">
              The automatic payment of <strong>£{amount?.toFixed(2) || '0.00'}</strong> failed.
            </p>
            <p className="text-xs text-red-600 mt-1">
              Would you like to send an invoice to the customer?
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-100 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Send Options */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-zinc-700">Send invoice via:</p>
            
            {/* Email Option */}
            <button
              onClick={() => setSendEmail(!sendEmail)}
              className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                sendEmail 
                  ? 'border-emerald-500 bg-emerald-50' 
                  : 'border-zinc-200 bg-white hover:border-zinc-300'
              }`}
            >
              <div className={`p-2 rounded-lg ${sendEmail ? 'bg-emerald-500' : 'bg-zinc-100'}`}>
                <Mail size={20} className={sendEmail ? 'text-white' : 'text-zinc-400'} />
              </div>
              <div className="text-left flex-1">
                <p className={`font-medium ${sendEmail ? 'text-emerald-700' : 'text-zinc-700'}`}>
                  Email
                </p>
                <p className="text-xs text-zinc-500 truncate">
                  {customer?.email || 'No email on file'}
                </p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                sendEmail ? 'border-emerald-500 bg-emerald-500' : 'border-zinc-300'
              }`}>
                {sendEmail && <div className="w-2 h-2 bg-white rounded-full" />}
              </div>
            </button>

            {/* SMS Option */}
            <button
              onClick={() => setSendSms(!sendSms)}
              className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                sendSms 
                  ? 'border-emerald-500 bg-emerald-50' 
                  : 'border-zinc-200 bg-white hover:border-zinc-300'
              }`}
            >
              <div className={`p-2 rounded-lg ${sendSms ? 'bg-[#25d366]' : 'bg-zinc-100'}`}>
                <MessageSquare size={20} className={sendSms ? 'text-white' : 'text-zinc-400'} />
              </div>
              <div className="text-left flex-1">
                <p className={`font-medium ${sendSms ? 'text-emerald-700' : 'text-zinc-700'}`}>
                  WhatsApp / SMS
                </p>
                <p className="text-xs text-zinc-500 truncate">
                  {customer?.phone || 'No phone on file'}
                </p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                sendSms ? 'border-emerald-500 bg-emerald-500' : 'border-zinc-300'
              }`}>
                {sendSms && <div className="w-2 h-2 bg-white rounded-full" />}
              </div>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-zinc-100 flex gap-3">
          <button
            onClick={handleSkip}
            className="flex-1 px-4 py-3 text-zinc-600 hover:bg-zinc-100 rounded-xl transition-colors font-medium"
          >
            Skip
          </button>
          <button
            onClick={handleSendInvoice}
            disabled={loading || (!sendEmail && !sendSms)}
            className="flex-1 px-4 py-3 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Send size={18} />
                Send Invoice
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentFailureModal;
